import { Button, Popconfirm, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { SystemDict, SystemDictTableProps } from '../types'

const DictTable = ({
  items,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDelete,
  onAddChild,
  expandable = false,
}: SystemDictTableProps) => {
  const columns: ColumnsType<SystemDict> = useMemo(
    () => [
      {
        title: '字典名称',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '字典编码',
        dataIndex: 'code',
        key: 'code',
        width: 100,
      },
      {
        title: '排序',
        dataIndex: 'sort',
        key: 'sort',
        width: 80,
      },
      {
        title: '状态',
        dataIndex: 'isEnabled',
        key: 'isEnabled',
        width: 80,
        render: (isEnabled: boolean) => (
          <Tag color={isEnabled ? 'green' : 'default'}>
            {isEnabled ? '启用' : '停用'}
          </Tag>
        ),
      },
      {
        title: '备注',
        dataIndex: 'note',
        key: 'note',
        width: 100,
        ellipsis: true,
        render: (note?: string | null) => note || '-',
      },
      {
        title: '操作',
        key: 'action',
        width: onAddChild ? 280 : 170,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            {onAddChild
              ? (() => {
                  const canAddChild =
                    record.nodeType === 'ITEM' &&
                    typeof record.parentId === 'number' &&
                    record.parentId > 0
                  return (
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      disabled={!canAddChild}
                      onClick={() => onAddChild(record)}
                    >
                      新增子项
                    </Button>
                  )
                })()
              : null}
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确认删除"
              description={
                record.nodeType === 'CATEGORY'
                  ? `确定删除字典 "${record.name}" 吗？其下子项会一并删除。`
                  : `确定删除字典项 "${record.name}" 吗？`
              }
              onConfirm={() => onDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [onAddChild, onDelete, onEdit],
  )

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={items}
      loading={loading}
      rowClassName={(record) =>
        onSelectionChange && selectedIds.includes(record.id) ? 'ant-table-row-selected' : ''
      }
      onRow={(record) =>
        onSelectionChange
          ? {
              onClick: (event) => {
                const target = event.target as HTMLElement
                if (target.closest('button,a,.ant-popover,.ant-popconfirm')) return
                onSelectionChange([record.id])
              },
              style: { cursor: 'pointer' },
            }
          : {}
      }
      pagination={false}
      scroll={{ x: onAddChild ? 1000 : 700 }}
      expandable={expandable ? { defaultExpandAllRows: true } : undefined}
    />
  )
}

export default DictTable
